// @ts-nocheck

import React, { useMemo, createRef, useContext } from 'react';
import { Text, Group, Rect, Image } from 'react-konva';

import Formatter from '../../../i18n/formatter';
import { __ } from '../../../i18n/l10n';
import BetshareContext from '../contexts/BetshareContext';
import { useGetHeightsFromRefs } from '../hooks';
import { getRelativeValue } from '../utils';
import { getOutcomeLabelPosition, getOutcomeColor, fragmentText } from '../utils';

import BankerBadge from './BankerBadge';
import { getBoundsTitle, getBoundsBetTitle, getBoundsLabel, getBoundsOdd } from './commonBounds';
import TextWithBackground from './TextWithBackground';

const areas = ['titleFirstLine', 'titleRest', 'date', 'betTitle', 'label', 'marginTop', 'marginBottom'];

const Outcome = (props) => {
  const {
    x,
    y,
    matchTitle,
    sportId,
    matchStart,
    odds,
    betTitle,
    outcomeLabel,
    isBanker,
    isCancelled,
    isLast,
    outcomeHasOccured,
    forwardRef,
  } = props;

  const { images, stageWidth } = useContext(BetshareContext);

  const allRefs = useMemo(() => areas.reduce((acc, cur) => ({ ...acc, [cur]: createRef() }), {}), []);
  const heights = useGetHeightsFromRefs(allRefs);

  const boundsTitle = getBoundsTitle(stageWidth, !!matchStart);
  const boundsBetTitle = getBoundsBetTitle(stageWidth);
  const boundsLabel = getBoundsLabel(stageWidth);
  const boundsOdd = getBoundsOdd(stageWidth);

  const marginLeft = getRelativeValue(13, stageWidth);
  const marginTop = getRelativeValue(13, stageWidth);
  const marginBottom = getRelativeValue(isLast ? 20 : 13, stageWidth);

  const linesTitle = fragmentText(matchTitle.toUpperCase(), boundsTitle);
  const firstLineTitle = linesTitle.shift().text;
  const restLinesTitle = linesTitle.map((line) => line.text).join();
  const finalBetTitle = __('%1$s:', betTitle);

  const sportIconProps = useMemo(
    () => ({
      image: (sportId && images[`sportIcon${sportId}`]) || images.fallbackSportIcon,
      x: marginLeft - getRelativeValue(4, stageWidth),
      y: marginTop - getRelativeValue(3, stageWidth),
      width: getRelativeValue(22, stageWidth),
      height: getRelativeValue(22, stageWidth),
    }),
    [sportId, marginLeft, marginTop, stageWidth, images],
  );

  // define all y positions
  const yTitle = marginTop;
  const yDate = yTitle + (heights.titleFirstLine || 0) + (heights.titleRest || 0);
  const yBetTitle = yDate + (heights.date || 0);
  const { xLabel, yLabel, labelWidth } = getOutcomeLabelPosition(
    finalBetTitle,
    boundsBetTitle,
    outcomeLabel,
    boundsLabel,
  );
  const xOutcomeLabel = marginLeft + xLabel;
  const yOutcomeLabel = yBetTitle + yLabel;
  const yBottomRect = yOutcomeLabel + heights.label + marginBottom || 0;
  const yOdd = yOutcomeLabel + heights.label - boundsOdd.fontSize || 0;

  const backgroundLabel = getOutcomeColor(outcomeHasOccured, isCancelled);

  return (
    <Group x={x} y={y} ref={forwardRef}>
      {/* Empty Rect for margin top */}
      <Rect ref={allRefs.marginTop} x={0} y={0} width={1} height={marginTop} />
      <Image {...sportIconProps} />
      <Text
        ref={allRefs.titleFirstLine}
        text={firstLineTitle}
        x={getRelativeValue(33, stageWidth)}
        y={yTitle}
        width={boundsTitle.width}
        fontSize={boundsTitle.fontSize}
        lineHeight={boundsTitle.lineHeight}
        fontFamily={boundsTitle.fontFamily}
        fill="#fff"
      />
      {restLinesTitle !== '' && restLinesTitle !== ' ' ? (
        <Text
          ref={allRefs.titleRest}
          text={restLinesTitle}
          x={marginLeft}
          y={yTitle + boundsTitle.lineHeight * boundsTitle.fontSize}
          width={getRelativeValue(270, stageWidth)}
          fontSize={boundsTitle.fontSize}
          lineHeight={boundsTitle.lineHeight}
          fontFamily={boundsTitle.fontFamily}
          fill="#fff"
        />
      ) : (
        <Rect ref={allRefs.titleRest} x={0} y={0} width={0} height={0} />
      )}
      {matchStart ? (
        <Text
          ref={allRefs.date}
          text={Formatter.formatDate(matchStart, 'dateSlash')}
          x={marginLeft}
          y={yDate}
          width={getRelativeValue(270, stageWidth)}
          fontSize={getRelativeValue(12, stageWidth)}
          lineHeight={1.2}
          fontFamily="BettingPoliticaExtraBold, sans-serif"
          fill="#a9a9a9"
        />
      ) : (
        <Rect ref={allRefs.date} x={0} y={0} width={0} height={0} />
      )}
      <Text
        ref={allRefs.betTitle}
        text={betTitle ? finalBetTitle : null}
        x={marginLeft}
        y={yBetTitle}
        width={boundsBetTitle.width}
        fontSize={boundsBetTitle.fontSize}
        lineHeight={boundsBetTitle.lineHeight}
        fontFamily={boundsBetTitle.fontFamily}
        fill="#fff"
      />
      <TextWithBackground
        ref={allRefs.label}
        text={outcomeLabel}
        x={xOutcomeLabel}
        y={yOutcomeLabel}
        width={labelWidth}
        fontSize={boundsLabel.fontSize}
        lineHeight={boundsLabel.lineHeight}
        fontFamily={boundsLabel.fontFamily}
        backgroundColor={backgroundLabel}
        paddingTopBottom={boundsLabel.paddingTop}
        paddingLeftRight={boundsLabel.paddingLeft}
        cornerRadius={getRelativeValue(2, stageWidth)}
        fill="#fff"
      />

      {/* Badge for system bases */}
      {isBanker && <BankerBadge height={marginTop} stageWidth={stageWidth} />}

      {/*  Odds */}
      <Text
        text={Formatter.formatOdd(odds)}
        x={boundsOdd.x}
        y={yOdd}
        width={boundsOdd.width}
        height={boundsOdd.height}
        fontFamily={boundsOdd.fontFamily}
        align="center"
        lineHeight={boundsOdd.lineHeight}
        fontSize={boundsOdd.fontSize}
        fill="#fff"
      />

      {/* Empty Rect for margin bottom */}
      <Rect ref={allRefs.marginBottom} x={0} y={yBottomRect} width={1} height={1} />
    </Group>
  );
};

export default Outcome;
